import type { Lang } from "@/app/(landing)/i18n";

/**
 * Canonical feature matrix for each pricing tier, in both EN and NL.
 *
 * Consumed by the public /pricing page today; reserved for the billing page
 * ("what you get on your current plan") and upgrade paywalls tomorrow. Kept
 * next to Stripe `plans.ts` so the commercial truth — price, dbValue, what
 * the plan actually delivers — lives in one folder.
 *
 * TierKey is broader than PlanKey because "free" and "enterprise" are not
 * Stripe-purchasable: Free is the default, Enterprise is a sales conversation.
 *
 * RULE: every line here is a promise rendered next to a price, so anything that
 * doesn't ship today must say so inline — "(roadmap, Q4 2026)" — not sit in the
 * list unqualified. The REST API rows were unmarked for a while and read as
 * live on a €199/mo and a €12,000/yr tier while no API route, key issuance, or
 * per-account meter existed. Keep these in sync with the FAQ (faqContent.ts),
 * which is the other place the roadmap is stated.
 */

export type TierKey = "free" | "pass" | "pro" | "studio" | "enterprise";

/**
 * Display prices — the numbers a visitor reads, as opposed to the Stripe Price
 * IDs in plans.ts (which are env-mapped and carry no amount).
 *
 * Shared so /pricing and the landing page's price anchor can't disagree about
 * what PlotScore costs. Amounts are strings because they're typeset, not
 * computed: EN uses "1,990", NL "1.990".
 */
export const TIER_PRICING = {
  free: { en: "€0", nl: "€0" },
  pass: { en: "€39", nl: "€39" },
  proMonthly: { en: "49", nl: "49" },
  proAnnual: { en: "490", nl: "490" },
  studioMonthly: { en: "199", nl: "199" },
  studioAnnual: { en: "1,990", nl: "1.990" },
  /**
   * The annual plans typeset PER MONTH for the pricing tiles (490 / 12 and
   * 1,990 / 12, rounded); the billed yearly total is shown beside it. Keep in
   * step with the two lines above.
   */
  proAnnualPerMonth: { en: "41", nl: "41" },
  studioAnnualPerMonth: { en: "166", nl: "166" },
} as const;

export const TIER_FEATURES: Record<TierKey, Record<Lang, string[]>> = {
  free: {
    en: [
      "Complete composite score",
      "All five sub-scores visible",
      "Factor-by-factor breakdown",
      "In-app plot comparison",
      "Baseline deltas vs buurt & NL",
      "Viewing checklist on your phone",
    ],
    nl: [
      "Volledige samengestelde score",
      "Alle vijf subscores zichtbaar",
      "Factor-voor-factor analyse",
      "In-app plot vergelijking",
      "Baseline deltas vs. buurt & NL",
      "Bezichtigingschecklist op je telefoon",
    ],
  },
  pass: {
    en: [
      "Everything in Free",
      "Unlimited scores for 30 days",
      "Save & organize up to 50 plots",
      "PDF reports without watermarks",
      "Side-by-side comparison view",
      "Commute times to your places",
      "Monthly-cost breakdown (maandlasten)",
      "Price history & WOZ trajectory",
      "Memorial / monument rationales",
    ],
    nl: [
      "Alles uit Free",
      "Onbeperkte scores voor 30 dagen",
      "Bewaar & organiseer tot 50 panden",
      "PDF-rapporten zonder watermerk",
      "Vergelijking naast elkaar",
      "Reistijden naar jouw plekken",
      "Maandlasten-overzicht per woning",
      "Prijshistorie & WOZ-trajectorie",
      "Monument & gedenkplaats context",
    ],
  },
  pro: {
    en: [
      "Everything in Buyer's Pass",
      "Unlimited saved plots",
      "Client folders & tags",
      "Share-with-client read-only links",
      "CSV data export",
      "Change alerts on saved plots",
      "Multi-language reports (NL / EN / DE)",
      "Email support",
    ],
    nl: [
      "Alles uit Buyer's Pass",
      "Onbeperkt bewaarde panden",
      "Klantmappen & tags",
      "Deel-met-klant leesrechten-links",
      "CSV-export",
      "Wijzigingsmeldingen op bewaarde panden",
      "Meertalige rapporten (NL / EN / DE)",
      "E-mailondersteuning",
    ],
  },
  studio: {
    en: [
      // Live rows first, roadmap rows after — the tile shows the first six own
      // lines, so what €199 buys today must lead. Audited 2026-09-10 against
      // the code: bulk scoring, team seats, the twelve-plot matrix, the
      // history view and the nitrogen batch have no implementation behind
      // them (the compare page caps at four plots for every tier), and the
      // dashboard used to describe all five as delivered.
      "Everything in Pro",
      "City Atlas — every building in a town, scored",
      "Site Finder — reverse search across the G4 cities",
      "Priority support — same-day",
      "REST API — 10,000 scores / month (roadmap, Q4 2026)",
      "Bulk scoring (CSV → CSV) (roadmap)",
      "3 team seats (€29 / extra seat) (roadmap)",
      "Comparative matrix view (roadmap)",
      "Historical data & change tracking (roadmap)",
      "Nitrogen / heritage batch checker (roadmap)",
    ],
    nl: [
      "Alles uit Pro",
      "City Atlas — elk pand in een kern, gescoord",
      "Site Finder — omgekeerd zoeken door de G4-steden",
      "Prioriteitsondersteuning — dezelfde dag",
      "REST API — 10.000 scores / maand (roadmap, Q4 2026)",
      "Bulk scoring (CSV → CSV) (roadmap)",
      "3 teamplekken (€29 / extra plek) (roadmap)",
      "Vergelijkingsmatrix (roadmap)",
      "Historische data & wijzigingen (roadmap)",
      "Stikstof / erfgoed batch checker (roadmap)",
    ],
  },
  enterprise: {
    en: [
      "Everything in Studio, unmetered",
      "Unlimited API access (roadmap, Q4 2026)",
      "Custom data ingestion (your portfolio)",
      "White-label reports & dashboard",
      "Custom score weights & factors",
      "Dedicated account manager",
      "Signed SLA & DPA",
      "SSO (SAML / OIDC)",
    ],
    nl: [
      "Alles uit Studio, onbeperkt",
      "Onbeperkte API-toegang (roadmap, Q4 2026)",
      "Maatwerk data-integratie (jouw portefeuille)",
      "White-label rapporten & dashboard",
      "Aangepaste scoregewichten & factoren",
      "Eigen accountmanager",
      "Ondertekende SLA & DPA",
      "SSO (SAML / OIDC)",
    ],
  },
};
